import { opportunityLlmExtractionsService } from '@/services/opportunity-llm-extractions.service';
import { hasExtractionEnded } from '@/models/extraction-steps';

const PROGRESS_POLL_INTERVAL_MS = 30_000;

/**
 * Follow a running LLM job: subscribe to its SSE stream and poll the job's own
 * record as a fallback, so its ending is noticed even when the stream drops.
 * Polling the job by id (not the opportunity's newest job) matters when several
 * jobs of one opportunity run at once: an older one would otherwise never be
 * seen ending. Both stop when `signal` aborts.
 *
 * @param {Object} params
 * @param {string} params.jobId
 * @param {AbortSignal} params.signal
 * @param {Object} params.sseCallbacks - SSE event callbacks (see sse-stream-util)
 * @param {Function} params.onEndedResult - Called with the job record when the
 *   fallback poll finds it in a terminal state.
 * @returns {number} The fallback poll's interval id.
 */
export function followLlmJob({ jobId, signal, sseCallbacks, onEndedResult }) {
  opportunityLlmExtractionsService.subscribeToJob({ jobId, signal }, sseCallbacks);

  const timer = setInterval(async () => {
    if (signal.aborted) {
      clearInterval(timer);
      return;
    }
    try {
      const job = await opportunityLlmExtractionsService.getJob({ jobId, signal });
      if (signal.aborted || !job) return;
      if (hasExtractionEnded(job.status)) onEndedResult(job);
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') return;
    }
  }, PROGRESS_POLL_INTERVAL_MS);
  signal.addEventListener('abort', () => clearInterval(timer), { once: true });
  return timer;
}
